import { useMemo, useEffect } from "react";
import { GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import LeafletMap from "@/components/map/LeafletMap";
import US_STATES_GEO from "./us-states.geojson.json";
import { US_STATES } from "./states";

const CODE_TO_NAME = Object.fromEntries(US_STATES.map(([c, n]) => [c, n]));

// Zoom to the selected state's polygon (or reset to the continental US).
function FitToState({ selected }) {
  const map = useMap();
  useEffect(() => {
    if (!selected) { map.setView([37.8, -96], 4); return; }
    const name = CODE_TO_NAME[selected];
    const feat = US_STATES_GEO.features.find((f) => (f.properties.name || "") === name);
    if (feat) {
      try { map.fitBounds(L.geoJSON(feat).getBounds(), { padding: [20, 20] }); } catch (e) {}
    }
  }, [selected, map]);
  return null;
}

const SCALE = ["#6ee7b7", "#34d399", "#10b981", "#059669", "#047857", "#065f46"];
// Revenue heat: red (low) -> yellow (medium) -> green (high)
const REV_SCALE = ["#f87171", "#fb923c", "#fbbf24", "#a3e635", "#4ade80", "#22c55e"];
const THRESHOLDS = {
  shops: [1, 3, 6, 11, 26, 51],
  partners: [1, 2, 4, 8, 16, 26],
  revenue: [1, 500, 2000, 5000, 15000, 50000],
};
const GAP = "#374151";

function colorFor(count, metric) {
  if (!count) return GAP;
  const t = THRESHOLDS[metric] || THRESHOLDS.shops;
  const scale = metric === "revenue" ? REV_SCALE : SCALE;
  for (let i = t.length - 1; i >= 0; i--) if (count >= t[i]) return scale[i];
  return scale[0];
}

function valueFor(d, metric) {
  if (metric === "revenue") return d.revenue || 0;
  if (metric === "partners") return d.partners || 0;
  return d.shops || 0;
}

export default function CoverageMap({ states = [], metric = "shops", selected, onSelect }) {
  const lookup = useMemo(() => {
    const m = {};
    states.forEach((s) => { m[(s.name || "").toLowerCase()] = { shops: s.shops, partners: s.partners, code: s.state }; });
    return m;
  }, [states]);

  const version = useMemo(() => states.reduce((a, s) => a + s.shops * 1000 + s.partners, 0), [states]);

  const style = (feature) => {
    const d = lookup[(feature.properties.name || "").toLowerCase()] || {};
    const isSel = d.code && d.code === selected;
    return {
      fillColor: colorFor(valueFor(d, metric), metric),
      weight: isSel ? 3 : 1,
      color: isSel ? "#f8fafc" : "#0d1420",
      fillOpacity: 0.82,
    };
  };

  const onEach = (feature, layer) => {
    const d = lookup[(feature.properties.name || "").toLowerCase()] || {};
    const extra = metric === "revenue"
      ? `$${(d.revenue || 0).toLocaleString()} revenue`
      : `${d.shops || 0} shops · ${d.partners || 0} partner${(d.partners || 0) === 1 ? "" : "s"}`;
    layer.bindTooltip(`<strong>${feature.properties.name}</strong><br/>${extra}`, { sticky: true });
    layer.on({ click: () => onSelect?.(d.code) });
  };

  const legendVals = metric === "revenue"
    ? ["$0", "1", "500", "2k", "5k", "15k", "50k+"]
    : metric === "partners" ? ["0", "1", "2", "4", "8", "16", "26+"] : ["0", "1", "3", "6", "11", "26", "51+"];
  const legendColors = [GAP, ...(metric === "revenue" ? REV_SCALE : SCALE)];
  const legendTitle = metric === "revenue" ? "Revenue / state" : metric === "partners" ? "Partners / state" : "Shops / state";

  return (
    <div className="relative" data-testid="coverage-map">
      <LeafletMap center={[37.8, -96]} zoom={4} height={460}>
        <GeoJSON key={`${metric}-${version}-${selected || "none"}`} data={US_STATES_GEO} style={style} onEachFeature={onEach} />
        <FitToState selected={selected} />
      </LeafletMap>
      <div className="absolute bottom-3 right-3 z-[1000] rounded-lg border border-border bg-[#0d1420]/90 backdrop-blur px-3 py-2 text-[10px]">
        <div className="text-muted-foreground mb-1 font-medium uppercase tracking-wide">{legendTitle}</div>
        <div className="flex items-center gap-1">
          {legendColors.map((c, i) => (
            <div key={i} className="flex flex-col items-center">
              <span className="w-5 h-3 rounded-sm block" style={{ background: c }} />
              <span className="text-muted-foreground mt-0.5">{legendVals[i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
